// actions.ts
import { Asset, Name, SessionKit } from "@wharfkit/session";
import { WebRenderer } from "@wharfkit/web-renderer";
import { WalletPluginAnchor } from "@wharfkit/wallet-plugin-anchor";
import { WalletPluginCloudWallet } from "@wharfkit/wallet-plugin-cloudwallet";
import { WalletPluginWombat } from "@wharfkit/wallet-plugin-wombat";
import { JsonRpc } from "eosjs";

import * as actionTypes from "./action-types";
import * as constants from "../../constant";
import { loadDataAction, getTokenBalance } from "./loader"; // Import loadDataAction and getTokenBalance from loader.ts

const {
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_ERROR,
  WORLD_SELECT,
  EXPLORATION_SUCCESS,
  EXPLORATION_ERROR,
  BUY_WORLD,
  SHOW_ALERT,
  SHOW_LOADING,
  HIDE_LOADING,
  CLAIM_STAKE,
  STAKE_SUCCESS,
  BUY_GOLD,
  GET_LEADERBOARD,
  GET_CONFIGS,
  STAKE_TOOL_SUCCESS,
  LEVELUP_SUCCESS,
  ASSET_STAKE_SUCCESS,
  CLAIM_EXPLORATION_SUCCESS,
  GET_EXPLORATIONS,
  LEVELUP_BOOST_SUCCESS,
  GET_TOURNAMENT_LIST,
  ENTER_TOURNAMENT_SUCCESS,
  GET_GAME_STATUS,
  GET_GAME_STATUS_SUCCESS,
  GET_GAME_STATUS_FAILURE,
  GET_ASSETS,
  GET_ASSETS_SUCCESS,
  GET_ASSETS_FAILURE,
} = actionTypes;

const {
  chainEndpoint,
  GAME_CONTRACT,
  FEED_TOKEN_CONTRACT,
  SYSTEM_TOKEN_CONTRACT,
  TOKEN_CONTRACT,
  TOKEN_SYMBOL,
  GOLD_TOKEN_SYMBOL,
  SYSTEM_TOKEN_SYMBOL,
  FEED_TOKEN_SYMBOL,
} = constants;

const webRenderer = new WebRenderer();

// Initialize SessionKit
const sessionKit = new SessionKit({
  appName: "WaxDeer",
  chains: [chainEndpoint],
  ui: webRenderer,
  walletPlugins: [
    new WalletPluginAnchor(),
    new WalletPluginCloudWallet(),
    new WalletPluginWombat(),
  ],
});

export function isSessionKitReady() {
  return sessionKit || false;
}

export const ChangeEndPoint = (endpoint: string) => async (dispatch: any) => {
  try {
    sessionKit.chains[0].url = endpoint;
  } catch (e: any) {
    console.error("Failed to change endpoint:", e);
  }
};

const rpc = new JsonRpc(sessionKit.chains[0].url); // Default rpc instance for actions

// Utility function for table queries - using default rpc instance unless overridden
export const queryGameTable = async ({
  contract = GAME_CONTRACT,
  table,
  scope = GAME_CONTRACT,
  rpcOverride = rpc, // Default to the rpc instance defined above
  ...options
}: any) => {
  try {
    return await rpcOverride.get_table_rows({
      // Use rpcOverride (which defaults to 'rpc')
      json: true,
      code: contract,
      scope,
      table,
      show_payer: false,
      reverse: false,
      ...options,
    });
  } catch (e: any) {
    console.error("Table query error:", e);
    return { rows: [] };
  }
};

// Utility functions to handle loading and alerts
const withLoading =
  (action: any, loadingType = SHOW_LOADING) =>
    async (dispatch: any) => {
      dispatch({ type: loadingType });
      try {
        return await action(dispatch);
      } finally {
        dispatch({ type: HIDE_LOADING });
      }
    };

export const showAlert = (dispatch: any, type: string, msg: string) => {
  dispatch({ type: SHOW_ALERT, payload: { type, msg, show: true } });
};

// Action Functions - Using utilities and loadDataAction
export const AutoLogin = () =>
  withLoading(async (dispatch: any) => {
    try {
      const session: any = await sessionKit.restore();
      if (session) {
        await loadDataAction(
          dispatch,
          session.permissionLevel.actor,
          session,
          rpc
        );
      }
    } catch (e: any) {
      console.error("Auto login failed:", e);
    }
  });
// 1. First, fix the Reload function to make it properly dispatch and return a result
export const Reload = () => async (dispatch: any) => {
  try {
    dispatch({ type: SHOW_LOADING });
    const session: any = await sessionKit.restore();
    if (!session) {
      throw new Error("No active session found");
    }
    await loadDataAction(dispatch, session.permissionLevel.actor, session, rpc);
    return true;
  } catch (error) {
    console.error("Reload error:", error);
    dispatch({
      type: SHOW_ALERT,
      payload: { type: "error", msg: "Failed to reload data", show: true },
    });
    return false;
  } finally {
    dispatch({ type: HIDE_LOADING });
  }
};
export const WalletLogin = () =>
  withLoading(async (dispatch: any) => {
    try {
      const response = await sessionKit.login();
      const session: any = (await sessionKit.restore()) || response;
      if (!session) {
        return dispatch({ type: LOGIN_ERROR });
      }
      await loadDataAction(
        dispatch,
        session.permissionLevel.actor,
        session,
        rpc
      );
    } catch (e: any) {
      console.error("Wallet login error:", e);
    }
  });

export const BuyWorldAction = (world: number, callback: any) =>
  withLoading(async (dispatch: any) => {
    try {
      const session: any = await sessionKit.restore();

      const data = {
        account: GAME_CONTRACT,
        name: "buyworld",
        authorization: [session.permissionLevel],
        data: { world_id: world, user: session.actor?.toString() },
      };
      const result = await session.transact({ action: data });

      if (result) {
        dispatch({ type: BUY_WORLD, payload: { world } });
        showAlert(
          dispatch,
          "success",
          `Successfully purchased world-${world}.`
        );
        callback(true);
      } else {
        dispatch({
          type: EXPLORATION_ERROR,
          payload: { error: { message: "Buy World Error" } },
        });
        callback(false);
      }
    } catch (e: any) {
      console.error("Buy world error:", e);
      showAlert(dispatch, "error", e.toString());
      callback(false);
    }
  });

export const SetWorldAction = (world: number) => async (dispatch: any) => {
  dispatch({ type: WORLD_SELECT, payload: { world } });
};

// 3. Fix ClaimExploration to properly handle state updates and reloading
export const claimExploration =
  (exploreId: number) => async (dispatch: any) => {
    try {
      dispatch({ type: SHOW_LOADING });
      const session: any = await sessionKit.restore();
      if (!session) {
        throw new Error("No active session found");
      }

      const actions = [];
      actions.push({
        account: GAME_CONTRACT,
        name: "claimexplore",
        authorization: [session.permissionLevel],
        data: {
          explore_id: exploreId,
        },
      });

      const result = await session.transact({ actions });

      if (result) {
        dispatch({
          type: CLAIM_EXPLORATION_SUCCESS,
          payload: { exploreId },
        });
        dispatch({
          type: SHOW_ALERT,
          payload: {
            type: "success",
            msg: "Successfully claimed exploration rewards",
            show: true,
          },
        });

        // Explicitly await the reload
        await dispatch(Reload());
        return true;
      } else {
        throw new Error("Transaction failed");
      }
    } catch (e: any) {
      console.error("Exploration claim error:", e);
      dispatch({
        type: SHOW_ALERT,
        payload: { type: "error", msg: e.toString(), show: true },
      });
      return false;
    } finally {
      dispatch({ type: HIDE_LOADING });
    }
  };

// 2. Fix the Exploration function to properly handle success/failure
export const Exploration =
  (world: number, asset_ids: Array<any>) => async (dispatch: any) => {
    try {
      dispatch({ type: SHOW_LOADING });
      const session: any = await sessionKit.restore();
      if (!session) {
        throw new Error("No active session found");
      }

      const actions = [];
      actions.push({
        account: GAME_CONTRACT,
        name: "sendexplore",
        authorization: [session.permissionLevel],
        data: { user: session.actor, asset_ids: asset_ids, world_id: world },
      });

      const result = await session.transact({ actions });

      if (result) {
        dispatch({
          type: EXPLORATION_SUCCESS,
          payload: { world, asset_ids },
        });
        dispatch({
          type: SHOW_ALERT,
          payload: {
            type: "success",
            msg: "Exploration started successfully",
            show: true,
          },
        });

        // Explicitly await the reload to ensure data is refreshed
        await dispatch(Reload());
        return true;
      } else {
        throw new Error("Transaction failed");
      }
    } catch (e: any) {
      console.error("Exploration error:", e);
      dispatch({
        type: EXPLORATION_ERROR,
        payload: { error: e.toString() },
      });
      dispatch({
        type: SHOW_ALERT,
        payload: { type: "error", msg: e.toString(), show: true },
      });
      return false;
    } finally {
      dispatch({ type: HIDE_LOADING });
    }
  };

export const UnstakePool = (pool_id: number) =>
  withLoading(async (dispatch: any) => {
    try {
      const session: any = await sessionKit.restore();

      const data = {
        account: GAME_CONTRACT,
        name: "unstake",
        authorization: [session.permissionLevel],
        data: { stake_id: pool_id },
      };
      const result = await session.transact({ action: data });

      if (result) {
        showAlert(dispatch, "success", `Successfully unstaked `);
        return true;
      } else {
        dispatch({
          type: EXPLORATION_ERROR,
          payload: { error: { message: "Stake Pool Error" } },
        });
      }
    } catch (e: any) {
      console.error("Stake pool error:", e);
      showAlert(dispatch, "error", e.toString());
    }
  });

export const StakePool = (pool_id: number, amount: any) =>
  withLoading(async (dispatch: any) => {
    try {
      const session: any = await sessionKit.restore();
      const memo = `stake:${pool_id}`;
      const quantity = amount.toFixed(4) + " " + TOKEN_SYMBOL;

      const data = {
        account: GAME_CONTRACT,
        name: "addstake",
        authorization: [session.permissionLevel],
        data: { pool_id: pool_id, from: session.actor, quantity: quantity },
      };
      const result = await session.transact({ action: data });

      if (result) {
        dispatch({
          type: STAKE_SUCCESS,
          payload: { pool_id, amount: quantity },
        });
        showAlert(
          dispatch,
          "success",
          `Successfully staked ${amount} ${TOKEN_SYMBOL}`
        );
      } else {
        dispatch({
          type: EXPLORATION_ERROR,
          payload: { error: { message: "Stake Pool Error" } },
        });
      }
    } catch (e: any) {
      console.error("Stake pool error:", e);
      showAlert(dispatch, "error", e.toString());
    }
  });

export const ClaimStakePool = (stake_id: number) =>
  withLoading(async (dispatch: any) => {
    try {
      const session: any = await sessionKit.restore();

      const data = {
        account: GAME_CONTRACT,
        name: "claim",
        authorization: [session.permissionLevel],
        data: { user: session.actor, stake_id },
      };
      const result = await session.transact({ action: data });

      if (result) {
        dispatch({ type: CLAIM_STAKE, payload: { stake_id } });
        showAlert(dispatch, "success", "Successfully claimed reward");
      } else {
        showAlert(dispatch, "error", "Error while claiming reward");
      }
    } catch (e: any) {
      console.error("Claim stake pool error:", e);
      showAlert(dispatch, "error", e.toString());
    }
  });

// 5. Fix the ClaimTools function
export const ClaimTools = (ids: Array<any>) => async (dispatch: any) => {
  try {
    dispatch({ type: SHOW_LOADING });
    const session: any = await sessionKit.restore();
    if (!session) {
      throw new Error("No active session found");
    }

    // Extract asset_ids properly, handling both array of objects and array of strings
    const asset_ids = ids?.map((item: any) =>
      typeof item === 'string' ? item : item.asset_id || item
    ).filter(id => id); // Filter out any undefined/null values

    if (!asset_ids.length) {
      throw new Error("No valid asset IDs to claim");
    }

    // Log the actual IDs being sent to help with debugging
    console.log("Claiming tools with asset IDs:", asset_ids);

    const data = {
      account: GAME_CONTRACT,
      name: "claimtools",
      authorization: [session.permissionLevel],
      data: { user: session.actor, asset_ids: asset_ids },
    };

    // Calculate expected rewards for better user feedback
    let tokens = [] as any;
    for (const item of ids) {
      if (!item.tool_config) continue;
      var symbol = item.tool_config?.reward_token?.split(" ")[1];
      var amount = parseFloat(item.tool_config?.reward_token?.split(" ")[0] || "0");
      if (!isNaN(amount) && symbol) {
        if (tokens.find((token: any) => token.symbol === symbol)) {
          tokens.find((token: any) => token.symbol === symbol).amount += amount;
        } else {
          tokens.push({ symbol, amount });
        }
      }
    }

    const result = await session.transact({ action: data });

    if (result) {
      dispatch({
        type: STAKE_TOOL_SUCCESS,
        payload: { ids: asset_ids },
      });

      const messages = [];
      for (const token of tokens) {
        messages.push(`${token.amount.toFixed(4)} ${token.symbol}`);
      }
      const rewardMessage = messages.length
        ? `You got ${messages.join(" and ")}`
        : "You got 0 rewards";

      dispatch({
        type: SHOW_ALERT,
        payload: { type: "success", msg: rewardMessage, show: true },
      });

      // No need to await reload here - let the UI handle it
      return true;
    } else {
      throw new Error("Transaction failed");
    }
  } catch (e: any) {
    console.error("Claim tools error:", e);
    dispatch({
      type: SHOW_ALERT,
      payload: { type: "error", msg: e.toString(), show: true },
    });
    return false;
  } finally {
    dispatch({ type: HIDE_LOADING });
  }
};

// 4. Fix the StakeTools function
export const StakeTools =
  (ids: Array<any>, reward_amount: number, type: string) =>
    async (dispatch: any) => {
      try {
        dispatch({ type: SHOW_LOADING });
        const session: any = await sessionKit.restore();
        if (!session) {
          throw new Error("No active session found");
        }

        const data = {
          account: GAME_CONTRACT,
          name: "staketool",
          authorization: [session.permissionLevel],
          data: { user: session.actor, asset_ids: ids, type: 1 },
        };

        const result = await session.transact({ action: data });

        if (result) {
          // Explicitly await the reload
          await dispatch(Reload());
          return true;
        } else {
          throw new Error("Transaction failed");
        }
      } catch (e: any) {
        console.error("Stake tools error:", e);
        dispatch({
          type: SHOW_ALERT,
          payload: { type: "error", msg: e.toString(), show: true },
        });
        // Still reload to make sure the UI is in sync with the current state
        await dispatch(Reload());
        return false;
      } finally {
        dispatch({ type: HIDE_LOADING });
      }
    };

export const DepositTokenAction = (depositAmount: any, depositSymbol: any) =>
  withLoading(async (dispatch: any) => {
    try {
      const session: any = await sessionKit.restore();
      const memo = "deposit";
      const contract =
        depositSymbol === "ANTLERS" ? TOKEN_CONTRACT : FEED_TOKEN_CONTRACT;
      const quantity = Number(depositAmount).toFixed(4) + " " + depositSymbol;

      const data = {
        account: contract,
        name: "transfer",
        authorization: [session.permissionLevel],
        data: { from: session.actor, to: GAME_CONTRACT, quantity, memo },
      };
      const result = await session.transact({ action: data });

      if (result) {
        showAlert(
          dispatch,
          "success",
          `Successfully deposited ${depositAmount} ${depositSymbol}.`
        );
        await Reload();
      } else {
        showAlert(dispatch, "error", "Deposit Error");
      }
    } catch (e: any) {
      console.error("Deposit token error:", e);
      showAlert(dispatch, "error", e.toString());
    }
  });

export const WithdrawTokenAction = (depositSymbol: any, withdrawAmount: any) =>
  withLoading(async (dispatch: any) => {
    try {
      const session: any = await sessionKit.restore();

      const quantity = Number(withdrawAmount).toFixed(4) + " " + depositSymbol;

      const data = {
        account: GAME_CONTRACT,
        name: "withdrawtkn",
        authorization: [session.permissionLevel],
        data: { user: session.actor, quantity },
      };
      const result = await session.transact({ action: data });

      if (result) {
        showAlert(
          dispatch,
          "success",
          `Successfully withdrew ${withdrawAmount} ${depositSymbol}.`
        );
        await Reload();
      } else {
        showAlert(dispatch, "error", "Withdraw Error");
      }
    } catch (e: any) {
      console.error("Withdraw token error:", e);
      showAlert(dispatch, "error", e.toString());
    }
  });
export const BuyGameTokenAction = (amount: any, receive_amount: any) =>
  withLoading(async (dispatch: any) => {
    try {
      const session: any = await sessionKit.restore();
      const memo = "buy:gold";
      const quantity = Number(amount).toFixed(8) + " " + SYSTEM_TOKEN_SYMBOL;

      const data = {
        account: SYSTEM_TOKEN_CONTRACT,
        name: "transfer",
        authorization: [session.permissionLevel],
        data: { from: session.actor, to: GAME_CONTRACT, quantity, memo },
      };
      const result = await session.transact({ action: data });

      if (result) {
        dispatch({ type: BUY_GOLD, payload: { amount, receive_amount } });
        showAlert(
          dispatch,
          "success",
          `Successfully bought ${receive_amount} ${GOLD_TOKEN_SYMBOL}.`
        );
        await Reload();
      } else {
        showAlert(dispatch, "error", "Buy Gold Error");
      }
    } catch (e: any) {
      console.error("Buy game token error:", e);
      showAlert(dispatch, "error", e.toString());
    }
  });

export const DeerStake = (asset_ids: any) =>
  withLoading(async (dispatch: any) => {
    try {
      const session: any = await sessionKit.restore();

      const data = {
        account: GAME_CONTRACT,
        name: "stakedeer",
        authorization: [session.permissionLevel],
        data: {
          user: session.actor,
          asset_ids: asset_ids,
        },
      };
      const result = await session.transact({ action: data });

      if (result) {
        showAlert(
          dispatch,
          "success",
          `Successfully staked ${asset_ids.length} assets`
        );
        dispatch({
          type: ASSET_STAKE_SUCCESS,
          payload: { asset_ids },
        });
        await Reload();
      } else {
        showAlert(dispatch, "error", "Stake Error");
      }
    } catch (e: any) {
      console.error("Stake error:", e);
      showAlert(dispatch, "error", e.toString());
    }
  });


export const ToolUpgrade = (
  asset_id: any
) => withLoading(async (dispatch: any) => {
  try {
    const session: any = await sessionKit.restore();
    const data = {
      account: GAME_CONTRACT,
      name: "upgradetool",
      authorization: [session.permissionLevel],
      data: {
        user: session.actor,
        asset_ids: [asset_id]
      },
    };
    const result = await session.transact({ action: data });
    if (result) {
      await dispatch(Reload());
      showAlert(
        dispatch,
        "success",
        `Successfully leveled up asset ${asset_id}`
      );
      return true;
    } else {
      showAlert(dispatch, "error", "Level up Error");
    }
  } catch (e: any) {
    console.error("Level upgrade error:", e);
    await dispatch(Reload());
    showAlert(dispatch, "error", e.toString());
    return false;
  }
});


export const DeerUpgrade = (
  asset_id: any,
  current_level: any,
  targetLevel: number
) =>
  withLoading(async (dispatch: any) => {
    try {
      const session: any = await sessionKit.restore();
      const data = {
        account: GAME_CONTRACT,
        name: "upgradedeer",
        authorization: [session.permissionLevel],
        data: {
          user: session.actor,
          asset_ids: [asset_id],
          targetLevel: targetLevel,
        },
      };
      const result = await session.transact({ action: data });

      if (result) {
        // dispatch({
        //   type: LEVELUP_SUCCESS,
        //   payload: { asset_id, current_level },
        // });
        await dispatch(Reload());
        showAlert(
          dispatch,
          "success",
          `Successfully leveled up asset ${asset_id}`
        );
        return true;
      } else {
        showAlert(dispatch, "error", "Level up Error");
      }
    } catch (e: any) {
      console.error("Level upgrade error:", e);
      await dispatch(Reload());
      showAlert(dispatch, "error", e.toString());
      return false;
    }
  });

export const LevelUpgrade = (
  asset_id: any,
  current_level: any,
  targetLevel: number
) =>
  withLoading(async (dispatch: any) => {
    try {
      const session: any = await sessionKit.restore();
      const memo = `upgrade:${asset_id}`;
      const quantity =
        ((current_level * current_level) / 2).toFixed(4) +
        " " +
        FEED_TOKEN_SYMBOL;

      const data = {
        account: FEED_TOKEN_CONTRACT,
        name: "transfer",
        authorization: [session.permissionLevel],
        data: { from: session.actor, to: GAME_CONTRACT, quantity, memo },
      };
      const result = await session.transact({ action: data });

      if (result) {
        dispatch({
          type: LEVELUP_SUCCESS,
          payload: { asset_id, current_level },
        });
        showAlert(
          dispatch,
          "success",
          `Successfully leveled up asset ${asset_id}`
        );
      } else {
        showAlert(dispatch, "error", "Level up Error");
      }
    } catch (e: any) {
      console.error("Level upgrade error:", e);
      showAlert(dispatch, "error", e.toString());
    }
  });

export const LevelupBoost = (asset_id: any, boostAmount: number) =>
  withLoading(async (dispatch: any) => {
    try {
      const session: any = await sessionKit.restore();

      const data = {
        account: GAME_CONTRACT,
        name: "boostlvlup",
        authorization: [session.permissionLevel],
        data: { user: session.actor, asset_id, amount: boostAmount },
      };
      const result = await session.transact({ action: data });

      if (result) {
        dispatch({
          type: LEVELUP_BOOST_SUCCESS,
          payload: { asset_id, boostAmount },
        });
        showAlert(
          dispatch,
          "success",
          `Successfully boosted asset ${asset_id}`
        );
      } else {
        showAlert(dispatch, "error", "Boost Error");
      }
    } catch (e: any) {
      console.error("Level up boost error:", e);
      showAlert(dispatch, "error", e.toString());
    }
  });

export const StartTournament = (
  id: number | null 
) => async (dispatch: any) => {
  try{
    const session: any = await sessionKit.restore();
    if (id == null)
      throw new Error("One or more required parameters are missing or invalid");
    const result = await session.transact({
      action: {
        account: GAME_CONTRACT,
        name: "startmatch",
        authorization: [session.permissionLevel],
        data: { id },
      },
    });

    if (result) {
      dispatch({
        type: SHOW_ALERT,
        payload: { type: "success", msg: "Successfully Started tournament", show: true },
      });
      await dispatch(Reload());
    }
  } catch (e: any) {
    dispatch({
      type: SHOW_ALERT,
      payload: { type: "error", msg: e.toString(), show: true },
    });
  }
};

export const ClearTournament = (
  id: number | null 
) => async (dispatch: any) => {
  try{
    const session: any = await sessionKit.restore();
    if (id == null)
      throw new Error("One or more required parameters are missing or invalid");
    const result = await session.transact({
      action: {
        account: GAME_CONTRACT,
        name: "cleartour",
        authorization: [session.permissionLevel],
        data: { id },
      },
    });

    if (result) {
      dispatch({
        type: SHOW_ALERT,
        payload: { type: "success", msg: "Successfully cleared tournament", show: true },
      });
      await dispatch(Reload());
    }
    else{
      showAlert(dispatch, "error", "Clear tournament Error");
    }
  }
  catch(e: any){
    dispatch({
      type: SHOW_ALERT,
      payload: { type: "error", msg: e.toString(), show: true },
    });
  }
}

export const DeclareResultTournament = (
  id: number | null 
) => async (dispatch: any) => {
  try{
const session: any = await sessionKit.restore();
    if (id == null)
      throw new Error("One or more required parameters are missing or invalid");
    const result = await session.transact({
      action: {
        account: GAME_CONTRACT,
        name: "declaretour",
        authorization: [session.permissionLevel],
        data: { id },
      },
    });

    if (result) {
      dispatch({
        type: SHOW_ALERT,
        payload: { type: "success", msg: "Successfully Started tournament", show: true },
      });
      await dispatch(Reload());
    }
  } catch (e: any) {
    dispatch({
      type: SHOW_ALERT,
      payload: { type: "error", msg: e.toString(), show: true },
    });
  }
};

export const CreateTournament = (
  id: number | null,
  reg_start: number | null,
  reg_end: number | null,
  start_time: number | null,
  end_time: number | null,
  min_level: number | null,
  max_level: number | null,
  schema: any[],
  max_players: number | null
) => async (dispatch: any) => {
  try {
    const session: any = await sessionKit.restore();
    if (
      id == null ||
      reg_start == null ||
      reg_end == null ||
      start_time == null ||
      end_time == null ||
      min_level == null ||
      max_level == null ||
      !Array.isArray(schema) ||
      max_players == null
    ) {
      throw new Error("One or more required parameters are missing or invalid");
    }

    const tournamentData = {
      id,
      fees: "1.0000 ANTLERS",
      reg_start,
      reg_end,
      start_time,
      end_time,
      nft_pool: [],
      rules: {
        max_players,
        schema,
        min_level,
        max_level
      }
    };
    
    const result = await session.transact({
      action: {
        account: GAME_CONTRACT,
        name: "createtour",
        authorization: [session.permissionLevel],
        data: {
          data: tournamentData
        },
      },
    });

    if (result) {
      dispatch({
        type: SHOW_ALERT,
        payload: { type: "success", msg: "Successfully created tournament", show: true },
      });
      await dispatch(Reload());
    }
  } catch (e: any) {
    dispatch({
      type: SHOW_ALERT,
      payload: { type: "error", msg: e.toString(), show: true },
    });
  }
};

export const EnterTournament =
  (asset_ids: Array<any>, id: any) => async (dispatch: any) => {
    try {
      const session: any = await sessionKit.restore();

      const data = {
        account: GAME_CONTRACT,
        name: "entertour",
        authorization: [session.permissionLevel],
        data: { player: session.actor, asset_ids, id },
      };
      const result = await session.transact({ action: data });
      if (result) {
        dispatch({
          type: SHOW_ALERT,
          payload: { type: "success", msg: "Successfully entered in Tournament", show: true },
        });
        dispatch({ type: ENTER_TOURNAMENT_SUCCESS, payload: { id } });
        await dispatch(Reload());
      }
    } catch (e: any) {
      console.error("Enter tournament error:", e);
      dispatch({
        type: SHOW_ALERT,
        payload: { type: "error", msg: e.toString(), show: true },
      });
    }
  };

export const GetTournamentList = () => async (dispatch: any) => {
  try {
    // Use correct table names as defined in your smart contract
    const { rows: tournaments_config } = await queryGameTable({ table: "tourconfigs", limit: 1000 });
    // Throttle requests to avoid "too many request" errors
    const batchQuery = async (configs: any[], batchSize: number) => {
      const results: any[] = [];
      for (let i = 0; i < configs.length; i += batchSize) {
        const batch = configs.slice(i, i + batchSize);
        const batchResults = await Promise.all(
          batch.map((config: any) =>
            queryGameTable({ table: "tourmatches", limit: 1000, scope: config.id })
              .then(res => res.rows)
          )
        );
        results.push(...batchResults);
        if (i + batchSize < configs.length) {
          await new Promise(res => setTimeout(res, 300));
        }
      }
      return results;
    };

    const allMatches = await batchQuery(tournaments_config, 10);
    const allTournaments = await queryGameTable({ table: "tournaments", limit: 1000 }).then(res => res.rows) || [];
    const allResults = await queryGameTable({ table: "tresult", limit: 1000 }).then(res => res.rows) || [];

    const matches = allMatches.flat();
    const tournaments = allTournaments;
    const tour_result = allResults;

    const enrichedConfigs = Array.isArray(tournaments_config)
      ? tournaments_config.map((config: any) => {
        const configMatches = matches.filter((match: any) => match.id === config.id);
        const entry = tournaments.find((entry: any) => entry.id === config.id);
        const result = tour_result.find((result: any) => result.id === config.id);
        let entries = [];
        if (entry && Array.isArray(entry.entries))
          entries = entry.entries;

        return {
          ...config,
          matches: configMatches,
          entries,
          results: result,
          total_fees: entry?.fee_counter || "0.0000 ANTLERS"
        };
      })
      : [];
    dispatch({ type: GET_TOURNAMENT_LIST, payload: { tournaments: enrichedConfigs } });
  } catch (e: any) {
    console.error("Get tournament list error:", e);
    showAlert(dispatch, "error", e.toString());
  }
};

export const GetConfigs = () => async (dispatch: any) => {
  try {
    const result = await queryGameTable({ table: "worldinfo", limit: 1000 }); // Using default rpc
    const data = result.rows;
    dispatch({ type: GET_CONFIGS, payload: { data } });
  } catch (e: any) {
    console.error("Get configs error:", e);
    showAlert(dispatch, "error", e.toString());
  }
};

export const GetLeaderBoard = () => async (dispatch: any) => {
  try {
    const session: any = await sessionKit.restore();

    const [leaderboardResult, myScoreResult] = await Promise.all([
      queryGameTable({ table: "leaderboard", limit: 1000 }), // Using default rpc
      queryGameTable({
        table: "leaderboard",
        limit: 1,
        lower_bound: session.actor?.toString(),
        upper_bound: session.actor?.toString(),
      }), // Using default rpc
    ]);

    // Transform response if needed to match the contract structure
    var leaderboard = leaderboardResult.rows.map((row: any) => ({
      username: row.username,
      deer_used: row.deer_used,
      explore_count: row.explore_count,
      reward: row.reward,
    }));

    //filter out username = f4wqy.wam
    leaderboard = leaderboard.filter(
      (item: any) => item.username !== "f4wqy.wam"
    );

    const myScore =
      myScoreResult.rows.length > 0
        ? {
          username: myScoreResult.rows[0].username,
          deer_used: myScoreResult.rows[0].deer_used,
          explore_count: myScoreResult.rows[0].explore_count,
          reward: myScoreResult.rows[0].reward,
        }
        : null;

    dispatch({ type: GET_LEADERBOARD, payload: { leaderboard, myScore } });
  } catch (e: any) {
    console.error("Get leaderboard error:", e);
    showAlert(dispatch, "error", e.toString());
  }
};

export const ShowAlert =
  (type: string, msg: string, show: boolean) => async (dispatch: any) => {
    dispatch({ type: SHOW_ALERT, payload: { type, msg, show } });
  };

export const WalletLogout = () => async (dispatch: any) => {
  dispatch({
    type: LOGIN_SUCCESS,
    payload: {
      session: null,
      balance: 0,
      assets: [],
      world_cost: [],
      open_worlds: [1],
      reward_info: [],
      tool_configs: [],
      deer_configs: [],
      staking_pool: [],
      pool_info: [],
    },
  });
};

export const LoginRequest = (id: number) => ({
  type: LOGIN_REQUEST,
  payload: {},
});

export const showLoading = (isShow: boolean) => async (dispatch: any) => {
  dispatch({ type: isShow ? SHOW_LOADING : HIDE_LOADING, payload: {} });
};

// Export utility functions if you need them in loader.ts or other parts of your app
export { withLoading };

export const ClaimExploreAction = (exploreId: number) =>
  withLoading(async (dispatch: any) => {
    try {
      const session: any = await sessionKit.restore();

      const data = {
        account: GAME_CONTRACT,
        name: "claimexplore",
        authorization: [session.permissionLevel],
        data: { explore_id: exploreId },
      };

      const result = await session.transact({ action: data });

      if (result) {
        dispatch({ type: CLAIM_EXPLORATION_SUCCESS, payload: { exploreId } });
        showAlert(
          dispatch,
          "success",
          "Successfully claimed exploration rewards"
        );
        await Reload();
        return true;
      } else {
        showAlert(dispatch, "error", "Failed to claim exploration");
        return false;
      }
    } catch (e: any) {
      console.error("Claim exploration error:", e);
      showAlert(dispatch, "error", e.toString());
      return false;
    }
  });

// Add new action functions for Dashboard
export const GetGameStatus = () => async (dispatch: any) => {
  try {
    dispatch({ type: actionTypes.GET_GAME_STATUS });
    dispatch({ type: SHOW_LOADING });

    // Get session for authentication
    const session: any = await sessionKit.restore();
    if (!session) {
      throw new Error("No active session found");
    }

    // Fetch relevant data for game status
    const [worldInfoResult, userWorldsResult, explorationsResult] = await Promise.all([
      queryGameTable({ table: "worldinfo", limit: 100 }),
      queryGameTable({
        table: "userworld",
        limit: 1,
        lower_bound: session.actor?.toString(),
        upper_bound: session.actor?.toString()
      }),
      queryGameTable({
        table: "explores",
        limit: 100,
        index_position: 2,
        key_type: "name",
        lower_bound: session.actor?.toString(),
        upper_bound: session.actor?.toString()
      })
    ]);

    // Process and structure the data
    const worldsInfo = worldInfoResult.rows || [];
    const openWorlds = userWorldsResult.rows.length > 0
      ? userWorldsResult.rows[0].openworlds || [1]
      : [1]; // Default to world 1 if no data
    const explorations = explorationsResult.rows || [];

    // Calculate completed and active explorations
    const completedExplorations = explorations.filter((exp: any) =>
      exp.end_time && exp.end_time <= Math.floor(Date.now() / 1000)
    );
    const activeExplorations = explorations.filter((exp: any) =>
      !exp.end_time || exp.end_time > Math.floor(Date.now() / 1000)
    );

    // Success dispatch with structured data
    dispatch({
      type: actionTypes.GET_GAME_STATUS_SUCCESS,
      payload: {
        worldsInfo,
        openWorlds,
        explorations,
        completedExplorations,
        activeExplorations,
        lastUpdated: Date.now()
      }
    });

    return true;
  } catch (error: any) {
    console.error("Get game status error:", error);
    dispatch({
      type: actionTypes.GET_GAME_STATUS_FAILURE,
      payload: { error: error.toString() }
    });
    dispatch({
      type: SHOW_ALERT,
      payload: { type: "error", msg: "Failed to load game status", show: true }
    });
    return false;
  } finally {
    dispatch({ type: HIDE_LOADING });
  }
};

export const GetAssets = () => async (dispatch: any) => {
  try {
    dispatch({ type: actionTypes.GET_ASSETS });
    dispatch({ type: SHOW_LOADING });

    // Get session for authentication
    const session: any = await sessionKit.restore();
    if (!session) {
      throw new Error("No active session found");
    }

    // Fetch both tool configs and deer configs
    const [toolConfigsResult, deerConfigsResult, stakingPoolsResult] = await Promise.all([
      queryGameTable({ table: "toolinfos", limit: 100 }),
      queryGameTable({ table: "deerconfigs", limit: 100 }),
      queryGameTable({ table: "stakingpools", limit: 100 })
    ]);

    // Fetch user's staked tools and deers
    const [toolsResult, deersResult] = await Promise.all([
      queryGameTable({
        table: "tools",
        limit: 1000,
        index_position: 2,
        key_type: "name",
        lower_bound: session.actor?.toString(),
        upper_bound: session.actor?.toString()
      }),
      queryGameTable({
        table: "deers",
        limit: 1000,
        index_position: 2,
        key_type: "name",
        lower_bound: session.actor?.toString(),
        upper_bound: session.actor?.toString()
      })
    ]);

    // Extract and process the results
    const toolConfigs = toolConfigsResult.rows || [];
    const deerConfigs = deerConfigsResult.rows || [];
    const stakingPools = stakingPoolsResult.rows || [];
    const tools = toolsResult.rows || [];
    const deers = deersResult.rows || [];

    // Enhance tools with their configuration data
    const enhancedTools = tools.map((tool: any) => {
      const config = toolConfigs.find((config: any) => config.template_id === tool.template_id);
      return { ...tool, tool_config: config };
    });

    // Enhance deers with their configuration data
    const enhancedDeers = deers.map((deer: any) => {
      const config = deerConfigs.find((config: any) => config.template_id === deer.template_id);
      return { ...deer, deer_config: config };
    });

    // Success dispatch with processed data
    dispatch({
      type: actionTypes.GET_ASSETS_SUCCESS,
      payload: {
        toolConfigs,
        deerConfigs,
        stakingPools,
        tools: enhancedTools,
        deers: enhancedDeers,
        lastUpdated: Date.now()
      }
    });

    return true;
  } catch (error: any) {
    console.error("Get assets error:", error);
    dispatch({
      type: actionTypes.GET_ASSETS_FAILURE,
      payload: { error: error.toString() }
    });
    dispatch({
      type: SHOW_ALERT,
      payload: { type: "error", msg: "Failed to load game assets", show: true }
    });
    return false;
  } finally {
    dispatch({ type: HIDE_LOADING });
  }
};
